import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import STORE from '../../../../store';
import Loading from '../../../../components/Loading/Loading';
import FlexCentered from '../../../../components/Common/FlexCentered';
import UsersContext from '../../../../contexts/UsersContext';
import isDefined from '../../../../isDefined';
import CurrentUserContext from '../../../../contexts/CurrentUserContext';
import ImpersonatorContext from '../../../../contexts/ImpersonatorContext';

export const GroupsContext = React.createContext<{
  groups: UserManagement.Group[];
  isLoadingGroups: boolean;
  getUsersInGroup: (
    groupId: string,
  ) => (UserManagement.SignedUpUser | UserManagement.PendingUser)[];
  groupsCurrentUserIsIn: UserManagement.Group[];
}>({
  groups: [],
  isLoadingGroups: true,
  getUsersInGroup: () => [],
  groupsCurrentUserIsIn: [],
});

const useGroups = () => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [groups, setGroups] = useState<UserManagement.Group[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    STORE.userManagement
      .getGroupsRef({ accountId: selectedAccountId })
      .onSnapshot((snapshot) => {
        const newGroups: UserManagement.Group[] = [];
        snapshot.forEach((doc) => {
          newGroups.push(doc.data());
        });
        setGroups(_.sortBy(newGroups, 'name'));
        setIsLoading(false);
      });
  }, [selectedAccountId]);

  return { groups, isLoadingGroups: isLoading };
};

const GroupsProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { signedUpAndPendingUsers } = useContext(UsersContext);
  const { groups, isLoadingGroups } = useGroups();
  const getUsersInGroup = useCallback(
    (
      groupId: string,
    ): (UserManagement.SignedUpUser | UserManagement.PendingUser)[] => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) {
        return [];
      }

      return group.userIds
        .map((uid) => signedUpAndPendingUsers.find((u) => u.id === uid))
        .filter(isDefined);
    },
    [signedUpAndPendingUsers, groups],
  );

  const groupsCurrentUserIsIn = useMemo((): UserManagement.Group[] => {
    const userId = impersonatorId ? impersonatorId : currentUserId;
    return groups.filter((g) => g.userIds.includes(userId));
  }, [currentUserId, groups, impersonatorId]);

  if (isLoadingGroups) {
    return (
      <FlexCentered style={{ height: '100%' }}>
        <Loading />
      </FlexCentered>
    );
  }

  return (
    <GroupsContext.Provider
      value={{
        groups,
        isLoadingGroups,
        getUsersInGroup,
        groupsCurrentUserIsIn,
      }}
    >
      {children}
    </GroupsContext.Provider>
  );
};

export default GroupsProvider;
